import {
  Alert,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Group,
  NumberInput,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconArrowLeftBar,
  IconCalculator,
  IconChecklist,
  IconFileText,
  IconInfoCircle,
  IconTargetArrow,
} from "@tabler/icons-react";
import { FormEvent, ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../../seo/Seo";
import {
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
} from "../../seo/utils";
import GetHelpForm from "../Shared/GetHelpForm";
import "./ApBioScoreCalculatorPage.css";

type ScoreKey = "mcq" | "frq";
type ScoreValue = number | "";
type ScoreInputs = Record<ScoreKey, ScoreValue>;
type ScoreErrors = Partial<Record<ScoreKey, string>>;

type SectionConfig = {
  key: ScoreKey;
  label: string;
  helper: string;
  max: number;
  weight: number;
  icon: JSX.Element;
};

const TOOL_PATH = "/tools/ap-bio-score-calculator";
const TOOLS_PATH = "/tools";
const seoTitle = "AP Bio Score Calculator | Estimate Your AP Biology Score";
const seoDescription =
  "Use SubjectBuddy's AP Bio score calculator to estimate your AP Biology exam score from multiple-choice and free-response practice scores.";

const sectionConfigs: SectionConfig[] = [
  {
    key: "mcq",
    label: "Multiple Choice Score",
    helper: "Enter your multiple-choice score out of 60.",
    max: 60,
    weight: 50,
    icon: <IconChecklist size={20} />,
  },
  {
    key: "frq",
    label: "Free Response Score",
    helper: "Enter your estimated free-response score out of 60.",
    max: 60,
    weight: 50,
    icon: <IconFileText size={20} />,
  },
];

const scoreMeanings: Record<number, string> = {
  5: "Excellent AP Biology performance",
  4: "Strong AP Biology performance",
  3: "Generally considered qualified",
  2: "May need improvement",
  1: "Needs significant improvement",
};

const examRows = [
  ["Multiple Choice", "60 questions", "50%"],
  ["Free Response", "6 questions", "50%"],
];

const conversionRows = [
  ["75-100%", "5", "Extremely well qualified"],
  ["60-74%", "4", "Well qualified"],
  ["45-59%", "3", "Qualified"],
  ["35-44%", "2", "Possibly qualified"],
  ["0-34%", "1", "No recommendation"],
];

const relatedTools = [
  {
    title: "APUSH Score Calculator",
    path: "/tools/apush-score-calculator",
    description:
      "Use the APUSH score calculator to estimate your AP U.S. History practice exam score.",
  },
  {
    title: "AP Chem Score Calculator",
    path: "/tools/ap-chem-score-calculator",
    description:
      "Use the AP Chem score calculator to estimate AP Chemistry practice performance.",
  },
  {
    title: "AP Lang Score Calculator",
    path: "/tools/ap-lang-score-calculator",
    description:
      "Use the AP Lang score calculator to estimate AP English Language practice performance.",
  },
  {
    title: "AP CSP Score Calculator",
    path: "/tools/ap-csp-score-calculator",
    description:
      "Use the AP CSP score calculator to estimate AP Computer Science Principles scores.",
  },
  {
    title: "AP Psych Score Calculator",
    path: "/tools/ap-psych-score-calculator",
    description:
      "Use the AP Psych score calculator to estimate AP Psychology practice performance.",
  },
  {
    title: "AP Human Geography Score Calculator",
    path: "/tools/ap-human-geography-score-calculator",
    description:
      "Use the AP Human Geography score calculator to estimate AP Human Geography practice performance.",
  },
  {
    title: "AP Grading Scale",
    path: "/tools/ap-grading-scale",
    description:
      "Use the AP Grading Scale guide to understand score meanings from 1 to 5.",
  },
];

const faqs = [
  {
    question: "Is this AP Bio score calculator accurate?",
    answer:
      "This AP Bio score calculator gives an estimated score based on common AP Biology exam weighting. It is useful for practice, but official score conversions may vary each year.",
  },
  {
    question: "Can I use this as an AP Bio exam score calculator?",
    answer:
      "Yes. You can use this as an AP Bio exam score calculator by entering your multiple-choice and free-response scores from a practice exam.",
  },
  {
    question: "Is this AP Bio test score calculator useful for practice tests?",
    answer:
      "Yes. This AP Bio test score calculator is designed for students who want to estimate their AP Biology score after completing a practice test.",
  },
  {
    question: "How does the AP grading scale work?",
    answer:
      "The AP grading scale reports scores from 1 to 5. A score of 3 is usually considered qualified, while 4 and 5 are stronger scores. Exact conversions may vary by exam year.",
  },
  {
    question: "What is a good AP Bio score?",
    answer:
      "A score of 3 is commonly considered qualified, while a 4 or 5 is a stronger result. Many students use an AP Bio calculator during practice to understand whether they are on track.",
  },
  {
    question: "How do I calculate my AP Bio score?",
    answer:
      "To calculate your AP Bio score, enter your multiple-choice score out of 60 and your free-response score out of 60. The calculator applies a 50% weight to each section and estimates your AP score from 1 to 5.",
  },
  {
    question: "Where can I find other AP score calculators?",
    answer:
      "You can also use the APUSH score calculator for AP U.S. History practice, or browse the tools page for grade and final exam calculators.",
  },
];

function getNumericScore(value: ScoreValue) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function getEstimatedScore(weightedPercentage: number) {
  if (weightedPercentage >= 75) {
    return 5;
  }

  if (weightedPercentage >= 60) {
    return 4;
  }

  if (weightedPercentage >= 45) {
    return 3;
  }

  if (weightedPercentage >= 35) {
    return 2;
  }

  return 1;
}

function calculateResult(scores: ScoreInputs) {
  const mcqPercentage = (getNumericScore(scores.mcq) / 60) * 100;
  const frqPercentage = (getNumericScore(scores.frq) / 60) * 100;
  const weightedPercentage = (mcqPercentage * 0.5) + (frqPercentage * 0.5);
  const estimatedScore = getEstimatedScore(weightedPercentage);

  return {
    mcqPercentage,
    frqPercentage,
    weightedPercentage,
    estimatedScore,
    meaning: scoreMeanings[estimatedScore],
  };
}

function validateScores(scores: ScoreInputs) {
  const nextErrors: ScoreErrors = {};

  if (scores.mcq === "" || scores.mcq === null || scores.mcq === undefined) {
    nextErrors.mcq = "Multiple Choice score is required.";
  } else if (
    typeof scores.mcq !== "number" ||
    Number.isNaN(scores.mcq) ||
    scores.mcq < 0 ||
    scores.mcq > 60
  ) {
    nextErrors.mcq = "Multiple Choice score must be between 0 and 60.";
  }

  if (scores.frq === "" || scores.frq === null || scores.frq === undefined) {
    nextErrors.frq = "Free Response score is required.";
  } else if (
    typeof scores.frq !== "number" ||
    Number.isNaN(scores.frq) ||
    scores.frq < 0 ||
    scores.frq > 60
  ) {
    nextErrors.frq = "Free Response score must be between 0 and 60.";
  }

  return nextErrors;
}

function ApBioScoreCalculatorPage() {
  const navigate = useNavigate();
  const [scores, setScores] = useState<ScoreInputs>({
    mcq: "",
    frq: "",
  });
  const [errors, setErrors] = useState<ScoreErrors>({});
  const [result, setResult] = useState<ReturnType<typeof calculateResult> | null>(
    null,
  );

  function updateScore(key: ScoreKey, value: string | number) {
    setScores((currentScores) => ({
      ...currentScores,
      [key]: value === "" ? "" : Number(value),
    }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      [key]: undefined,
    }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validateScores(scores);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      setResult(null);
      return;
    }

    setResult(calculateResult(scores));
  }

  const softwareApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "AP Bio Score Calculator",
    applicationCategory: "EducationalApplication",
    operatingSystem: "Web",
    description:
      "A free AP Bio score calculator that helps students estimate their AP Biology score from practice test results.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  };

  return (
    <Container fluid className="apbio-page" px={0}>
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={TOOL_PATH}
        ogTitle="AP Bio Score Calculator"
        ogDescription="Estimate your AP Biology score using multiple-choice and free-response practice results."
        keywords={[
          "ap bio score calculator",
          "ap bio exam score calculator",
          "ap bio calculator score",
          "ap score calculator bio",
          "ap bio test score calculator",
          "how to calculate ap bio score",
          "ap biology score calculator",
          "ap grading scale",
          "ap bio score calculator 2026",
        ]}
        schema={[
          createOrganizationSchema(),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tools", path: TOOLS_PATH },
            { name: "AP Bio Score Calculator", path: TOOL_PATH },
          ]),
          createFaqSchema(faqs),
          softwareApplicationSchema,
        ]}
      />

      <Box className="apbio-hero">
        <Container size="xl">
          <Stack gap="lg">
            <Breadcrumbs separator="/">
              <Anchor component={Link} to="/">
                Home
              </Anchor>
              <Anchor component={Link} to={TOOLS_PATH}>
                Tools
              </Anchor>
              <Text>AP Bio Score Calculator</Text>
            </Breadcrumbs>

            <Button
              color="teal"
              variant="subtle"
              leftSection={<IconArrowLeftBar />}
              onClick={() => navigate(TOOLS_PATH)}
              className="apbio-back-button"
            >
              Back to Tools
            </Button>

            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing={36}>
              <Stack gap="lg" justify="center">
                <Group gap="sm">
                  <Badge color="teal" size="lg" radius="sm">
                    AP Biology
                  </Badge>
                  <Badge color="green" size="lg" radius="sm" variant="light">
                    Free Student Tool
                  </Badge>
                </Group>
                <Title order={1} className="apbio-title">
                  AP Bio Score Calculator
                </Title>
                <Text size="lg" c="dimmed" className="apbio-hero-copy">
                  Use this AP Bio score calculator to estimate your AP Biology
                  exam score before the official result. Enter your
                  multiple-choice and free-response scores to get a quick
                  prediction of your AP score from 1 to 5.
                </Text>
                <Alert
                  icon={<IconInfoCircle size={18} />}
                  color="teal"
                  radius="lg"
                  variant="light"
                >
                  This AP Bio score calculator gives an estimated score based on
                  common AP Biology exam weighting. Official AP score
                  conversions may vary by exam year.
                </Alert>
              </Stack>

              <section aria-labelledby="apbio-calculator-heading">
                <Paper
                  withBorder
                  radius="xl"
                  p="xl"
                  className="apbio-tool-card"
                >
                  <form onSubmit={handleSubmit} noValidate>
                    <Stack gap="lg">
                      <Stack gap={4}>
                        <Title id="apbio-calculator-heading" order={2}>
                          Estimate Your AP Biology Score
                        </Title>
                        <Text c="dimmed">
                          This AP Bio calculator gives an estimated score for
                          practice and planning only. Official AP Biology score
                          conversions may change each year.
                        </Text>
                      </Stack>

                      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                        {sectionConfigs.map((section) => (
                          <Paper
                            key={section.key}
                            withBorder
                            radius="lg"
                            p="lg"
                            className="apbio-input-card"
                          >
                            <Stack gap="sm">
                              <Group justify="space-between" align="flex-start">
                                <Group gap="sm" wrap="nowrap">
                                  <ThemeIcon
                                    color="teal"
                                    variant="light"
                                    radius="xl"
                                  >
                                    {section.icon}
                                  </ThemeIcon>
                                  <Stack gap={0}>
                                    <Text
                                      component="label"
                                      htmlFor={`apbio-${section.key}`}
                                      fw={800}
                                    >
                                      {section.label}
                                    </Text>
                                    <Text
                                      id={`apbio-${section.key}-helper`}
                                      size="sm"
                                      c="dimmed"
                                    >
                                      {section.helper}
                                    </Text>
                                  </Stack>
                                </Group>
                                <Badge color="blue" variant="light" radius="sm">
                                  {section.weight}%
                                </Badge>
                              </Group>

                              <NumberInput
                                id={`apbio-${section.key}`}
                                value={scores[section.key]}
                                min={0}
                                max={section.max}
                                clampBehavior="strict"
                                onChange={(value) =>
                                  updateScore(section.key, value)
                                }
                                error={errors[section.key]}
                                rightSection={
                                  <Text size="xs" c="dimmed">
                                    /{section.max}
                                  </Text>
                                }
                                aria-label={section.label}
                                aria-describedby={`apbio-${section.key}-helper`}
                                required
                              />
                            </Stack>
                          </Paper>
                        ))}
                      </SimpleGrid>

                      <Button
                        type="submit"
                        size="lg"
                        radius="lg"
                        color="teal"
                        leftSection={<IconCalculator />}
                      >
                        Calculate AP Bio Score
                      </Button>

                      {result ? (
                        <Paper
                          withBorder
                          radius="xl"
                          p="lg"
                          className="apbio-result-card"
                          aria-live="polite"
                        >
                          <Stack gap="lg">
                            <Group justify="space-between" align="center">
                              <Stack gap={2}>
                                <Text size="sm" c="dimmed" fw={800} tt="uppercase">
                                  Result
                                </Text>
                                <Title order={3}>
                                  Estimated AP Score: {result.estimatedScore}
                                </Title>
                              </Stack>
                              <ThemeIcon size={58} radius="xl" color="teal">
                                <IconTargetArrow size={30} />
                              </ThemeIcon>
                            </Group>

                            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                              <Paper
                                radius="lg"
                                p="md"
                                className="apbio-mini-stat"
                              >
                                <Text fw={900} size="xl">
                                  {result.weightedPercentage.toFixed(1)}%
                                </Text>
                                <Text size="sm" c="dimmed">
                                  Estimated Weighted Percentage
                                </Text>
                              </Paper>
                              <Paper
                                radius="lg"
                                p="md"
                                className="apbio-mini-stat"
                              >
                                <Text fw={900} size="xl">
                                  {result.meaning}
                                </Text>
                                <Text size="sm" c="dimmed">
                                  Score meaning
                                </Text>
                              </Paper>
                            </SimpleGrid>

                            <Divider />

                            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                              <Paper
                                withBorder
                                radius="lg"
                                p="md"
                                className="apbio-breakdown-card"
                              >
                                <Text fw={800}>Multiple Choice</Text>
                                <Text size="sm" c="dimmed">
                                  {getNumericScore(scores.mcq)} / 60
                                </Text>
                                <Text mt={8} fw={700}>
                                  {result.mcqPercentage.toFixed(1)}%
                                </Text>
                              </Paper>
                              <Paper
                                withBorder
                                radius="lg"
                                p="md"
                                className="apbio-breakdown-card"
                              >
                                <Text fw={800}>Free Response</Text>
                                <Text size="sm" c="dimmed">
                                  {getNumericScore(scores.frq)} / 60
                                </Text>
                                <Text mt={8} fw={700}>
                                  {result.frqPercentage.toFixed(1)}%
                                </Text>
                              </Paper>
                            </SimpleGrid>

                            <Alert
                              icon={<IconInfoCircle size={18} />}
                              color="blue"
                              radius="lg"
                              variant="light"
                            >
                              These score bands are approximate. Actual AP
                              Biology score cutoffs can vary by exam year.
                            </Alert>
                          </Stack>
                        </Paper>
                      ) : null}
                    </Stack>
                  </form>
                </Paper>
              </section>
            </SimpleGrid>
          </Stack>
        </Container>
      </Box>

      <Container size="xl" py={48}>
        <div className="apbio-layout">
          <main className="apbio-main">
            <article className="apbio-content">
              <SeoSection title="What Is an AP Bio Score Calculator?">
                <Text>
                  An AP Bio score calculator is a student tool that estimates
                  your AP Biology exam score using your practice test results.
                  Instead of guessing your final AP score, you can enter your
                  multiple-choice and free-response scores to see an estimated
                  score from 1 to 5.
                </Text>
                <Text>
                  This AP Biology score calculator is useful when you want a
                  quick score estimate before the official AP result.
                </Text>
              </SeoSection>

              <SeoSection title="How the AP Biology Exam Is Scored">
                <Text>
                  The AP Biology exam has two main sections: multiple choice
                  and free response. The multiple-choice section includes 60
                  questions and is worth 50% of the total exam score. The
                  free-response section includes 6 questions and is also worth
                  50% of the total exam score.
                </Text>
                <ResponsiveTable
                  headers={["Section", "Raw Score", "Exam Weight"]}
                  rows={examRows}
                />
              </SeoSection>

              <SeoSection title="AP Bio Exam Score Calculator Formula">
                <Text>
                  This AP Bio exam score calculator converts your
                  multiple-choice and free-response results into percentages,
                  applies the AP Biology exam weighting, and estimates your
                  final AP score from 1 to 5.
                </Text>
                <Paper withBorder radius="lg" p="lg" className="apbio-formula">
                  <Text fw={800}>Weighted Score =</Text>
                  <Text>
                    (MCQ percentage x 50) + (FRQ percentage x 50)
                  </Text>
                </Paper>
              </SeoSection>

              <SeoSection title="AP Bio Test Score Calculator for Practice Exams">
                <Text>
                  If you have just completed a practice exam, this AP Bio test
                  score calculator can help you check whether your current
                  performance is close to a 3, 4, or 5. It is useful for
                  tracking your AP Biology preparation before test day.
                </Text>
              </SeoSection>

              <SeoSection title="AP Score Calculator Bio Guide">
                <Text>
                  Students searching for an AP score calculator bio tool can
                  use this page to estimate their AP Biology score using the
                  main exam sections. The calculator is designed for practice,
                  planning, and revision support.
                </Text>
              </SeoSection>

              <SeoSection title="How to Calculate AP Bio Score">
                <Text>
                  To calculate your AP Bio score, first convert your
                  multiple-choice score into a percentage, then convert your
                  free-response score into a percentage. Since both sections are
                  worth 50%, the calculator averages both weighted sections and
                  estimates your final AP score.
                </Text>
              </SeoSection>

              <SeoSection title="AP Bio Calculator Score Conversion Table">
                <ResponsiveTable
                  headers={[
                    "Estimated Percentage",
                    "Predicted AP Score",
                    "Meaning",
                  ]}
                  rows={conversionRows}
                />
                <Text>
                  The AP grading scale reports final scores from 1 to 5, but
                  exact raw-score conversions may change by exam year. This
                  table is only an estimated guide for practice.
                </Text>
              </SeoSection>

              <SeoSection title="How Many Points Do You Need to Get a 5 on AP Bio?">
                <Text>
                  To get a 5 on AP Bio, students usually need strong
                  performance in both multiple-choice and free-response
                  sections. A high MCQ score and clear, accurate FRQ responses
                  can improve your estimated AP Biology score.
                </Text>
              </SeoSection>

              <SeoSection title="How Many Points Do You Need to Get a 3 on AP Bio?">
                <Text>
                  A score of 3 usually requires a balanced performance across
                  both AP Biology sections. This AP Bio score calculator can
                  help you test different score combinations and understand
                  which section needs more improvement.
                </Text>
              </SeoSection>

              <SeoSection title="AP Bio Exam Score Calculator vs AP Grading Scale">
                <Text>
                  An AP Bio exam score calculator estimates your score using
                  practice test results, while the AP grading scale shows the
                  final AP score range from 1 to 5. Since official AP score
                  conversions may change each year, this calculator should be
                  used as a helpful estimate rather than an official result.
                </Text>
              </SeoSection>

              <SeoSection title="Related AP Score Calculators">
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                  {relatedTools.map((tool) => (
                    <Paper
                      key={tool.path}
                      component={Link}
                      to={tool.path}
                      withBorder
                      radius="lg"
                      p="lg"
                      className="apbio-related-card"
                    >
                      <Stack gap="xs">
                        <Text fw={800} c="teal">
                          {tool.title}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {tool.description}
                        </Text>
                      </Stack>
                    </Paper>
                  ))}
                </SimpleGrid>
              </SeoSection>

              <SeoSection title="Related Student Support">
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                  <Paper
                    component={Link}
                    to="/services/biology-assignment-help"
                    withBorder
                    radius="lg"
                    p="lg"
                    className="apbio-related-card"
                  >
                    <Stack gap="xs">
                      <Text fw={800} c="teal">
                        Biology Assignment Help
                      </Text>
                      <Text size="sm" c="dimmed">
                        Get support with biology concepts, practice questions,
                        and coursework planning after estimating your AP score.
                      </Text>
                    </Stack>
                  </Paper>
                  <Paper
                    component={Link}
                    to="/assignment-help"
                    withBorder
                    radius="lg"
                    p="lg"
                    className="apbio-related-card"
                  >
                    <Stack gap="xs">
                      <Text fw={800} c="teal">
                        Assignment Help
                      </Text>
                      <Text size="sm" c="dimmed">
                        Use SubjectBuddy support to plan revision, coursework,
                        and study next steps across subjects.
                      </Text>
                    </Stack>
                  </Paper>
                </SimpleGrid>
              </SeoSection>

              <SeoSection title="AP Bio Score Calculator FAQs">
                <Stack gap="md">
                  {faqs.map((faq) => (
                    <Paper key={faq.question} withBorder radius="lg" p="lg">
                      <Title order={3}>{faq.question}</Title>
                      <Text c="dimmed">{faq.answer}</Text>
                    </Paper>
                  ))}
                </Stack>
              </SeoSection>
            </article>
          </main>

          <aside className="apbio-sidebar" aria-label="Study support form">
            <div className="apbio-sidebar-sticky">
              <Stack gap="md">
                <Paper withBorder radius="xl" p="lg">
                  <Stack gap="xs">
                    <Text size="sm" tt="uppercase" fw={800} c="teal">
                      Study support
                    </Text>
                    <Title order={3}>Need Help Preparing for AP Biology?</Title>
                    <Text c="dimmed" size="sm">
                      Share your practice score, exam timeline, or study goal
                      and we will help you plan the next revision step.
                    </Text>
                  </Stack>
                </Paper>
                <Paper withBorder radius="xl" p={0} className="apbio-help-form">
                  <GetHelpForm formSize="sm" flatForm />
                </Paper>
              </Stack>
            </div>
          </aside>
        </div>
      </Container>
    </Container>
  );
}

function SeoSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="apbio-seo-section">
      <Stack gap="md">
        <Title order={2}>{title}</Title>
        {children}
      </Stack>
    </section>
  );
}

function ResponsiveTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: string[][];
}) {
  return (
    <Box className="apbio-table-wrap">
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            {headers.map((header) => (
              <Table.Th key={header}>{header}</Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.join("-")}>
              {row.map((cell) => (
                <Table.Td key={cell}>{cell}</Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
}

export default ApBioScoreCalculatorPage;
